import {
  Button,
  Caption1,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Overflow,
  OverflowItem,
  Tab,
  TabList,
  makeStyles,
  useIsOverflowItemVisible,
  useOverflowMenu,
} from "@fluentui/react-components";
import { MoreHorizontalRegular } from "@fluentui/react-icons";
import dayjs from "dayjs";
import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { weekStartSelector } from "../../selectors/diningSelector";
import styles from "../../styles/menuPreview.module.css";
import MenuSection from "./MenuSection";
import { PreviewMenu } from "./types/menuPreviewTypes";

// ---------- Props ----------
export interface MenuWeekdaySelectorProps {
  weeklyMenu: Array<PreviewMenu[]>;
}

// ---------- Styles ----------
const useClasses = makeStyles({
  tabs: {
    marginTop: "8px",
  },
  overflowMenuButton: {
    alignSelf: "center",
  },
});

// ---------- Constants ----------
const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

// ---------- Overflow Menu Item ----------
type OverflowMenuItemProps = {
  id: string;
  label: string;
  onSelect: () => void;
};

const OverflowMenuItem = ({ id, label, onSelect }: OverflowMenuItemProps) => {
  const isVisible = useIsOverflowItemVisible(id);

  // Only render items that are actually overflowed
  if (isVisible) {
    return null;
  }

  return <MenuItem onClick={onSelect}>{label}</MenuItem>;
};

type OverflowMenuProps = {
  weekdayMenus: Array<PreviewMenu[]>;
  onSelect: (id: string) => void;
};

const OverflowMenu = ({ weekdayMenus, onSelect }: OverflowMenuProps) => {
  const classes = useClasses();
  const { ref, isOverflowing } = useOverflowMenu<HTMLButtonElement>();

  if (!isOverflowing) {
    return null;
  }

  return (
    <Menu>
      <MenuTrigger disableButtonEnhancement>
        <Button
          ref={ref}
          appearance="transparent"
          className={classes.overflowMenuButton}
          icon={<MoreHorizontalRegular />}
          aria-label="More days"
        />
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          {weekdayMenus.map((_, index) => {
            const id = index.toString();
            return <OverflowMenuItem key={id} id={id} label={WEEKDAYS[index]} onSelect={() => onSelect(id)} />;
          })}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
};

// ---------- Main Component ----------
const MenuWeekdaySelector = ({ weeklyMenu }: MenuWeekdaySelectorProps) => {
  const classes = useClasses();
  const weekStart = useSelector(weekStartSelector);

  // Remove weekends
  const weekdayMenus = useMemo(() => weeklyMenu.slice(1, 6), [weeklyMenu]);

  const [selectedDay, setSelectedDay] = useState("0");

  return (
    <div className={styles.menuPreviewContainer}>
      <Caption1>Menu for the week of {dayjs(weekStart).toDate().toLocaleDateString()}</Caption1>

      {/* Tabs with overflow */}
      <Overflow minimumVisible={1}>
        <TabList
          className={classes.tabs}
          selectedValue={selectedDay}
          onTabSelect={(_, data) => setSelectedDay(data.value as string)}
          appearance="subtle"
        >
          {weekdayMenus.map((_, index) => {
            const id = index.toString();
            return (
              <OverflowItem key={id} id={id} priority={id === selectedDay ? 2 : 1}>
                <Tab value={id} className={styles.pivotStyle}>
                  {WEEKDAYS[index]}
                </Tab>
              </OverflowItem>
            );
          })}

          <OverflowMenu weekdayMenus={weekdayMenus} onSelect={setSelectedDay} />
        </TabList>
      </Overflow>

      {/* Selected menu content */}
      {weekdayMenus.map(
        (week, index) =>
          selectedDay === index.toString() && <MenuSection key={WEEKDAYS[index]} previewMenuList={week} />,
      )}
    </div>
  );
};

export default MenuWeekdaySelector;
